// Martian Architecture Explorer -- renders frontend/public/architecture.json
// (generated by scripts/generate_architecture.py) as an interactive graph.
// This page is intentionally NOT wrapped in DashboardLayout: it needs full
// canvas width for the graph, and it's an internal engineering tool rather
// than a founder-facing surface -- see App.js's route comment for why it has
// no auth guard.
import { useEffect, useMemo, useState, useCallback } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap, Handle, Position, ReactFlowProvider, useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

const LAYER_COLORS = {
  actor: '#A88466', // Clay Deep (Martian palette); was violet
  frontend: '#2563eb',
  backend: '#0891b2',
  intelligence: '#d97706',
  data: '#16a34a',
  external: '#dc2626',
};

const TYPE_LABELS = {
  actor: 'Actor', page: 'Page', component: 'Component', hook: 'Hook',
  apiclient: 'API Client', apigroup: 'API Group', service: 'Service',
  collection: 'Collection', external: 'External', concept: 'Concept',
};

// Batch 9: five-value confidence taxonomy (replaces the old HIGH/MEDIUM).
// Colors chosen so LEGACY (a real gap worth noticing) reads as the loudest.
const CONFIDENCE_COLORS = {
  LIVE: '#16a34a', INFERRED: '#2563eb', GROUPED: '#b45309', LEGACY: '#dc2626', UNCERTAIN: '#64748b',
};
const CONFIDENCE_TAGS = {
  LIVE: null, INFERRED: '~inferred', GROUPED: '~grouped', LEGACY: '⚠ legacy', UNCERTAIN: '~uncertain',
};
const ALL_CONFIDENCE = ['LIVE', 'INFERRED', 'GROUPED', 'LEGACY', 'UNCERTAIN'];

// Batch 9: preset views. `layers: null` means "whatever the layer chips
// currently show"; `focus` is an optional allow-list of node ids -- when
// set, only those ids (within the allowed layers) render, so a view can
// zero in on one capability without permanently changing the layer chips.
const VIEW_PRESETS = {
  system: { label: 'System Architecture', layers: null, focus: null },
  intelligence_flow: {
    label: 'Intelligence Flow', layers: ['actor', 'frontend', 'backend', 'intelligence', 'data', 'external'],
    focus: [
      'page.signals', 'page.diagnoses', 'page.opportunities', 'page.recommendations', 'page.action_plans', 'page.som',
      'apiclient.intelligence', 'apiclient.core',
      'backend.api.signals', 'backend.api.diagnoses', 'backend.api.opportunities', 'backend.api.recommendations',
      'backend.api.action_plans', 'backend.api.som', 'backend.api.knowledge_bridge', 'backend.api.briefing', 'backend.api.v2_search_everywhere',
      'svc.knowledge_bridge', 'svc.som_pipeline', 'svc.signal', 'svc.diagnosis', 'svc.opportunity', 'svc.recommendation',
      'svc.action_plan', 'svc.measurement_loop', 'svc.knowledge', 'svc.entity', 'svc.legacy_engines',
      'col.som_scores', 'col.ai_responses', 'col.citations', 'col.knowledge_objects', 'col.signals', 'col.diagnoses',
      'col.opportunities', 'col.intelligence_recommendations', 'col.intelligence_action_plans', 'col.entities',
      'external.tavily',
    ],
  },
  frontend_backend: {
    label: 'Frontend → Backend', layers: ['actor', 'frontend', 'backend'], focus: null,
  },
  data: {
    label: 'Data / Database', layers: ['intelligence', 'data', 'external'], focus: null,
  },
  ownership: {
    label: 'Client / Tenant Ownership', layers: ['backend', 'intelligence', 'data'],
    focus: [
      'concept.user', 'concept.tenant', 'concept.client', 'concept.brand_entity',
      'col.users', 'col.tenants', 'col.clients', 'col.entities', 'col.company_context',
      'col.knowledge_objects', 'col.signals', 'col.diagnoses', 'col.opportunities',
      'col.intelligence_recommendations', 'col.intelligence_action_plans',
      'svc.entity', 'svc.company_context', 'svc.security_audit',
      'backend.api.clients', 'backend.api.auth',
    ],
  },
};

const COL_WIDTH = 320;
const ROW_HEIGHT = 76;
const SUBCOL_WIDTH = 260;
const MAX_ROWS_PER_SUBCOL = 16;

function layoutNodes(nodes, layers) {
  const byLayer = {};
  for (const n of nodes) {
    (byLayer[n.layer] = byLayer[n.layer] || []).push(n);
  }
  const positioned = [];
  const orderedLayers = [...layers].sort((a, b) => a.order - b.order);
  let xCursor = 0;
  for (const layer of orderedLayers) {
    const layerNodes = byLayer[layer.id] || [];
    const byType = {};
    for (const n of layerNodes) (byType[n.type] = byType[n.type] || []).push(n);
    let layerMaxSubcols = 0;
    let subcolIndex = 0;
    for (const type of Object.keys(byType).sort()) {
      const list = byType[type];
      const numSubcols = Math.max(1, Math.ceil(list.length / MAX_ROWS_PER_SUBCOL));
      for (let s = 0; s < numSubcols; s++) {
        const chunk = list.slice(s * MAX_ROWS_PER_SUBCOL, (s + 1) * MAX_ROWS_PER_SUBCOL);
        chunk.forEach((n, i) => {
          positioned.push({
            ...n,
            _x: xCursor + subcolIndex * SUBCOL_WIDTH,
            _y: i * ROW_HEIGHT + (type === Object.keys(byType).sort()[0] ? 0 : 0),
            _typeRowOffset: i,
          });
        });
        subcolIndex += 1;
      }
      layerMaxSubcols = Math.max(layerMaxSubcols, subcolIndex);
    }
    // stack type-groups vertically within their subcol lane instead of overlapping
    let yOffsetForType = 0;
    subcolIndex = 0;
    for (const type of Object.keys(byType).sort()) {
      const list = byType[type];
      const numSubcols = Math.max(1, Math.ceil(list.length / MAX_ROWS_PER_SUBCOL));
      for (let s = 0; s < numSubcols; s++) {
        const chunk = list.slice(s * MAX_ROWS_PER_SUBCOL, (s + 1) * MAX_ROWS_PER_SUBCOL);
        chunk.forEach((n, i) => {
          const p = positioned.find((pn) => pn.id === n.id);
          p._x = xCursor + subcolIndex * SUBCOL_WIDTH;
          p._y = i * ROW_HEIGHT;
        });
        subcolIndex += 1;
      }
    }
    xCursor += Math.max(1, layerMaxSubcols) * SUBCOL_WIDTH + 60;
  }
  return positioned;
}

function ArchNode({ data }) {
  const color = LAYER_COLORS[data.layer] || '#64748b';
  const confColor = CONFIDENCE_COLORS[data.confidence];
  const tag = CONFIDENCE_TAGS[data.confidence];
  return (
    <div
      style={{
        borderLeft: `4px solid ${color}`,
        background: data._dimmed ? 'var(--surface)' : 'white',
        opacity: data._dimmed ? 0.2 : 1,
        boxShadow: data._selected ? `0 0 0 2px ${color}` : (data._traced ? `0 0 0 2px ${confColor || color}` : '0 1px 2px rgba(0,0,0,0.08)'),
        borderRadius: 8,
        padding: '8px 10px',
        width: 236,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span>{TYPE_LABELS[data.type] || data.type}</span>
        {tag && (
          <span style={{ color: confColor, fontWeight: 800 }} title={CONFIDENCE_LEGEND_TEXT[data.confidence]}>{tag}</span>
        )}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.25, marginTop: 2 }}>
        {data.label}
      </div>
      {data.endpoints && data.endpoints.length > 0 && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{data.endpoints.length} endpoints • click to browse</div>
      )}
    </div>
  );
}

let CONFIDENCE_LEGEND_TEXT = {};

const nodeTypes = { arch: ArchNode };

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', color: '#475569' }}
    >
      {copied ? 'Copied' : 'Copy path'}
    </button>
  );
}

function EndpointsTable({ endpoints, onOpenFile }) {
  const [q, setQ] = useState('');
  const filtered = q.trim()
    ? endpoints.filter((e) => `${e.method} ${e.path} ${e.handler}`.toLowerCase().includes(q.toLowerCase()))
    : endpoints;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
        Endpoints ({endpoints.length})
      </div>
      <input
        value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter endpoints..."
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', marginBottom: 6 }}
      />
      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        {filtered.map((e, i) => (
          <div key={i} style={{ padding: '6px 8px', borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 11 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{
                fontWeight: 700, fontFamily: 'monospace', fontSize: 10, padding: '1px 5px', borderRadius: 4,
                background: { GET: '#dbeafe', POST: '#dcfce7', PUT: '#fef3c7', PATCH: '#fef3c7', DELETE: '#fee2e2' }[e.method] || '#f1f5f9',
              }}>{e.method}</span>
              <span style={{ fontFamily: 'monospace', color: '#0f172a' }}>{e.path}</span>
              {e.confidence === 'LEGACY' && <span style={{ color: '#dc2626', fontWeight: 700 }}>⚠ legacy</span>}
            </div>
            <div style={{ color: '#64748b', marginTop: 2 }}>
              <code>{e.handler}()</code> · {e.file}:{e.line}
              <button onClick={() => onOpenFile(e.file, e.line)} style={{ marginLeft: 6, fontSize: 9, border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }}>open</button>
            </div>
            <div style={{ color: '#64748b', marginTop: 2 }}>
              auth: {e.auth}{e.uses_get_verified_client ? ' + get_verified_client' : ''}
            </div>
            {e.tenant_client_scoping.length > 0 && (
              <div style={{ color: '#64748b' }}>scoped by: {e.tenant_client_scoping.join(', ')}</div>
            )}
            {e.service_calls.length > 0 && <div style={{ color: '#64748b' }}>calls: {e.service_calls.join(', ')}</div>}
            {e.db_collections.length > 0 && <div style={{ color: '#64748b' }}>collections: {e.db_collections.join(', ')}</div>}
            <div style={{ color: '#94a3b8' }}>request: {e.request_shape}{e.response_model ? ` → response_model: ${e.response_model}` : ''}</div>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: 8, fontSize: 11, color: '#94a3b8' }}>No matches.</div>}
      </div>
    </div>
  );
}

function DetailPanel({ node, allNodes, allEdges, onClose, onSelect, onTrace, traceActive, repoRoot }) {
  // Hooks must run every render regardless of `node` -- the early return
  // for the closed-panel case happens after all of them.
  const byId = useMemo(() => new Map(allNodes.map((n) => [n.id, n])), [allNodes]);
  const incoming = useMemo(
    () => (node ? allEdges.filter((e) => e.target === node.id).map((e) => ({ ...e, node: byId.get(e.source) })).filter((e) => e.node) : []),
    [allEdges, node, byId],
  );
  const outgoing = useMemo(
    () => (node ? allEdges.filter((e) => e.source === node.id).map((e) => ({ ...e, node: byId.get(e.target) })).filter((e) => e.node) : []),
    [allEdges, node, byId],
  );
  if (!node) return null;
  const Row = ({ label, value }) =>
    (value === undefined || value === null || (Array.isArray(value) && value.length === 0) || value === '') ? null : (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', fontWeight: 700 }}>{label}</div>
        {Array.isArray(value) ? (
          <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 13, color: '#1e293b' }}>
            {value.map((v, i) => <li key={i}>{v}</li>)}
          </ul>
        ) : (
          <div style={{ fontSize: 13, color: '#1e293b', marginTop: 2, whiteSpace: 'pre-wrap' }}>{String(value)}</div>
        )}
      </div>
    );
  const color = LAYER_COLORS[node.layer] || '#64748b';
  const confColor = CONFIDENCE_COLORS[node.confidence];

  const openFile = (file, line) => {
    if (!file || !repoRoot) return;
    const abs = `${repoRoot}/${file}`;
    window.open(`vscode://file/${abs}${line ? `:${line}` : ''}`, '_blank');
  };

  return (
    <div style={{
      position: 'absolute', top: 0, right: 0, bottom: 0, width: 420,
      background: 'white', borderLeft: '1px solid #e2e8f0', boxShadow: '-4px 0 16px rgba(0,0,0,0.06)',
      padding: 20, overflowY: 'auto', zIndex: 10,
    }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>×</button>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color, fontWeight: 700 }}>{TYPE_LABELS[node.type] || node.type} · {node.layer}</div>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 10px', color: '#0f172a' }}>{node.label}</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button onClick={() => onTrace(node.id)} style={{
          fontSize: 11, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
          border: `1px solid ${traceActive ? '#0f172a' : '#cbd5e1'}`,
          background: traceActive ? '#0f172a' : 'white', color: traceActive ? 'white' : '#0f172a', fontWeight: 600,
        }}>
          {traceActive ? 'Clear trace' : 'Trace architecture'}
        </button>
      </div>

      <div style={{ marginBottom: 12, padding: '8px 10px', background: '#f8fafc', borderRadius: 6, border: `1px solid ${confColor}33` }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: confColor, fontWeight: 800 }}>Confidence: {node.confidence}</div>
        <div style={{ fontSize: 12, color: '#334155', marginTop: 3 }}>{CONFIDENCE_LEGEND_TEXT[node.confidence]}</div>
      </div>

      <Row label="Purpose" value={node.purpose} />

      {node.file && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', fontWeight: 700 }}>Source file</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <code style={{ fontSize: 12, color: '#1e293b' }}>{node.file}</code>
            <CopyButton text={node.file} />
            {repoRoot && <button onClick={() => openFile(node.file)} style={{ fontSize: 10, border: '1px solid #cbd5e1', background: 'white', borderRadius: 4, padding: '2px 6px', cursor: 'pointer' }}>Open in editor</button>}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>"Open in editor" is best-effort (vscode:// URI) -- works only if VS Code is installed and registered as the handler.</div>
        </div>
      )}

      <Row label="Scoping keys" value={node.scoping_keys} />
      <Row label="Depends on" value={node.dependencies} />
      <Row label="APIs consumed" value={node.apis_consumed} />
      <Row label="DB collections" value={node.db_collections} />
      <Row label="External services" value={node.external_services} />
      <Row label="Metrics" value={node.metrics && Object.keys(node.metrics).length ? Object.entries(node.metrics).map(([k, v]) => `${k}: ${v}`) : null} />
      <Row label="Notes" value={node.detail} />
      <Row label="Tests" value={node.tests} />

      {node.endpoints && node.endpoints.length > 0 && <EndpointsTable endpoints={node.endpoints} onOpenFile={openFile} />}

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
          Who uses this? ({incoming.length})
        </div>
        {incoming.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Nothing in this graph points at it.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {incoming.map((e, i) => (
              <button key={i} onClick={() => onSelect(e.node.id)} title={e.label}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', color: '#0f172a' }}>
                {e.node.label} <span style={{ color: '#94a3b8' }}>({e.type})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
          What this uses ({outgoing.length})
        </div>
        {outgoing.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Nothing outgoing in this graph.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {outgoing.map((e, i) => (
              <button key={i} onClick={() => onSelect(e.node.id)} title={e.label}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', color: '#0f172a' }}>
                {e.node.label} <span style={{ color: '#94a3b8' }}>({e.type})</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// BFS both directions from `startId`, up to `depth` hops, over `edges`.
// Powers "Trace architecture" (point 6) and doubles as the reverse-trace
// data source for the DetailPanel's per-node "Who uses this?" lists.
// Generic UI-shell edges (every page -> DashboardLayout, every page ->
// useWorkspaceSelection) and "registered on the app" edges are real, but
// tracing THROUGH them turns any trace into "almost the whole graph" --
// every page shares the same layout/hook/entry hub. Excluding them keeps a
// trace meaningful: it still shows a page uses the layout (that edge is
// still drawn when the page itself is in the trace), it just doesn't let
// the layout become a bridge to every OTHER unrelated page.
const TRACE_HUB_EDGE_TYPES = new Set(['RENDERS_WITHIN', 'RENDERS', 'USES']);
const TRACE_HUB_NODE_IDS = new Set(['frontend.layout', 'apiclient.core', 'apiclient.intelligence', 'backend.entry']);

function traceSubgraph(startId, edges, depth = 4) {
  const adjacency = new Map();
  for (const e of edges) {
    if (!adjacency.has(e.source)) adjacency.set(e.source, []);
    if (!adjacency.has(e.target)) adjacency.set(e.target, []);
    const skip = TRACE_HUB_EDGE_TYPES.has(e.type) || TRACE_HUB_NODE_IDS.has(e.source) || TRACE_HUB_NODE_IDS.has(e.target);
    adjacency.get(e.source).push({ id: e.target, skip });
    adjacency.get(e.target).push({ id: e.source, skip });
  }
  const visited = new Set([startId]);
  let frontier = [startId];
  for (let hop = 0; hop < depth && frontier.length; hop++) {
    const next = [];
    for (const id of frontier) {
      for (const nb of adjacency.get(id) || []) {
        if (visited.has(nb.id)) continue;
        visited.add(nb.id);
        // A hub node itself becomes visible (so "this page uses the shared
        // layout" is still shown) but traversal does not continue FROM it,
        // and an edge whose own type is hub-ish never continues traversal
        // through its far end either.
        if (!nb.skip && !TRACE_HUB_NODE_IDS.has(nb.id)) next.push(nb.id);
      }
    }
    frontier = next;
  }
  return visited;
}

function GraphCanvas({ graph }) {
  const [search, setSearch] = useState('');
  const [activeLayers, setActiveLayers] = useState(() => new Set(graph.layers.map((l) => l.id)));
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('system');
  const [activeConfidence, setActiveConfidence] = useState(() => new Set(ALL_CONFIDENCE));
  const [traceId, setTraceId] = useState(null);
  const { fitView } = useReactFlow();

  const positioned = useMemo(() => layoutNodes(graph.nodes, graph.layers), [graph]);

  const matchIds = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    const s = new Set();
    for (const n of graph.nodes) {
      if (n.label.toLowerCase().includes(q) || (n.file || '').toLowerCase().includes(q) || (n.purpose || '').toLowerCase().includes(q)
        || (n.endpoints || []).some((ep) => `${ep.method} ${ep.path} ${ep.handler}`.toLowerCase().includes(q))) {
        s.add(n.id);
      }
    }
    return s;
  }, [search, graph.nodes]);

  const traceIds = useMemo(() => (traceId ? traceSubgraph(traceId, graph.edges) : null), [traceId, graph.edges]);

  const preset = VIEW_PRESETS[view];
  const viewLayers = useMemo(() => (preset.layers ? new Set(preset.layers) : activeLayers), [preset, activeLayers]);
  const viewFocus = useMemo(() => (preset.focus ? new Set(preset.focus) : null), [preset]);

  const applyView = useCallback((key) => {
    setView(key);
    const p = VIEW_PRESETS[key];
    if (p.layers) setActiveLayers(new Set(p.layers));
  }, []);

  const flowNodes = useMemo(() => {
    return positioned
      .filter((n) => viewLayers.has(n.layer))
      .filter((n) => !viewFocus || viewFocus.has(n.id))
      .filter((n) => activeConfidence.has(n.confidence))
      .map((n) => ({
        id: n.id,
        type: 'arch',
        position: { x: n._x, y: n._y },
        data: {
          ...n,
          _dimmed: (traceIds ? !traceIds.has(n.id) : false) || (!traceIds && matchIds ? !matchIds.has(n.id) : false),
          _selected: n.id === selectedId,
          _traced: traceIds ? traceIds.has(n.id) : false,
        },
      }));
  }, [positioned, viewLayers, viewFocus, activeConfidence, matchIds, selectedId, traceIds]);

  const visibleIds = useMemo(() => new Set(flowNodes.map((n) => n.id)), [flowNodes]);

  const flowEdges = useMemo(() => {
    return graph.edges
      .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
      .filter((e) => activeConfidence.has(e.confidence))
      .map((e, i) => {
        const touchesSelected = selectedId && (e.source === selectedId || e.target === selectedId);
        const inTrace = traceIds && traceIds.has(e.source) && traceIds.has(e.target);
        const dimmed = traceIds ? !inTrace : (matchIds ? !(matchIds.has(e.source) && matchIds.has(e.target)) : false);
        const edgeColor = e.confidence === 'LEGACY' ? '#dc2626' : (touchesSelected ? '#0f172a' : '#cbd5e1');
        return {
          id: `e${i}-${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          label: (touchesSelected || inTrace) ? e.label : undefined,
          animated: touchesSelected || inTrace,
          style: {
            stroke: edgeColor,
            strokeWidth: touchesSelected ? 2 : 1,
            strokeDasharray: e.confidence === 'INFERRED' ? '4 2' : (e.confidence === 'UNCERTAIN' ? '1 3' : undefined),
            opacity: dimmed ? 0.06 : (traceIds ? (inTrace ? 0.9 : 0.06) : (selectedId ? (touchesSelected ? 1 : 0.12) : 0.35)),
          },
          labelStyle: { fontSize: 10, fill: '#334155' },
        };
      });
  }, [graph.edges, visibleIds, selectedId, matchIds, activeConfidence, traceIds]);

  const selectedNode = graph.nodes.find((n) => n.id === selectedId) || null;

  const toggleLayer = useCallback((id) => {
    setView('system');
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleConfidence = useCallback((key) => {
    setActiveConfidence((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleTrace = useCallback((id) => {
    setTraceId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, traceId]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', gap: 8,
        alignItems: 'center', flexWrap: 'wrap', maxWidth: 'calc(100% - 440px)',
      }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search nodes + endpoints (name, file, purpose, path)..."
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, width: 280, background: 'white' }}
        />
        <button
          onClick={() => fitView({ padding: 0.15 })}
          style={{ padding: '6px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid #cbd5e1', background: 'white' }}
        >
          Fit view
        </button>
        {traceId && (
          <button onClick={() => setTraceId(null)} style={{ padding: '6px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: '1px solid #0f172a', background: '#0f172a', color: 'white' }}>
            Clear trace ({traceIds ? traceIds.size : 0} nodes)
          </button>
        )}
      </div>

      <div style={{ position: 'absolute', top: 12, right: traceId || selectedId ? 440 : 12, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.95)', padding: 4, borderRadius: 8, border: '1px solid #e2e8f0' }}>
          {Object.entries(VIEW_PRESETS).map(([key, p]) => (
            <button
              key={key}
              onClick={() => applyView(key)}
              style={{
                padding: '5px 9px', borderRadius: 6, fontSize: 10.5, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: view === key ? '#0f172a' : 'transparent', color: view === key ? 'white' : '#334155',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 320 }}>
          {graph.layers.map((l) => (
            <button
              key={l.id}
              onClick={() => toggleLayer(l.id)}
              style={{
                padding: '5px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${LAYER_COLORS[l.id]}`,
                background: (view === 'system' ? activeLayers.has(l.id) : viewLayers.has(l.id)) ? LAYER_COLORS[l.id] : 'white',
                color: (view === 'system' ? activeLayers.has(l.id) : viewLayers.has(l.id)) ? 'white' : LAYER_COLORS[l.id],
              }}
            >
              {l.name}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 320 }}>
          {ALL_CONFIDENCE.map((c) => (
            <button
              key={c}
              onClick={() => toggleConfidence(c)}
              title={CONFIDENCE_LEGEND_TEXT[c]}
              style={{
                padding: '4px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${CONFIDENCE_COLORS[c]}`,
                background: activeConfidence.has(c) ? CONFIDENCE_COLORS[c] : 'white',
                color: activeConfidence.has(c) ? 'white' : CONFIDENCE_COLORS[c],
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 12, left: 12, zIndex: 10, fontSize: 11, color: '#64748b',
        background: 'rgba(255,255,255,0.9)', padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
      }}>
        {graph.stats.node_count} nodes · {graph.stats.edge_count} relationships · {graph.stats.total_backend_routes} routes
        ({graph.stats.endpoints_with_detail} with full endpoint detail) · commit {graph.source_commit} · generated {new Date(graph.generated_at).toLocaleString()}
        <br />
        showing {flowNodes.length} nodes / {flowEdges.length} edges{traceId ? ' (trace mode)' : ''}
      </div>
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodeClick={(_, n) => setSelectedId(n.id)}
        onPaneClick={() => setSelectedId(null)}
        fitView
        minZoom={0.05}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={24} color="#e2e8f0" />
        <Controls />
        <MiniMap
          nodeColor={(n) => LAYER_COLORS[n.data?.layer] || '#94a3b8'}
          pannable zoomable
        />
      </ReactFlow>
      <DetailPanel
        node={selectedNode}
        allNodes={graph.nodes}
        allEdges={graph.edges}
        onClose={() => setSelectedId(null)}
        onSelect={setSelectedId}
        onTrace={handleTrace}
        traceActive={traceId === selectedId}
        repoRoot={graph.repo_root}
      />
    </div>
  );
}

export default function ArchitectureExplorerPage() {
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);

  // Security fix (Day 1 tenant-isolation audit, 2026-09-22): this used to
  // be a plain `fetch('/architecture.json')` -- a static file under
  // frontend/public/, served unauthenticated by CRA regardless of this
  // page's own PersonaGuard(admin/master) wrapper. Confirmed live: anyone,
  // logged in or not, could GET the full internal architecture graph
  // directly. Now fetched through the real, authenticated GET
  // /api/architecture route (server.py), which enforces the exact same
  // admin/master check server-side.
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/architecture`, { headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        CONFIDENCE_LEGEND_TEXT = data.confidence_legend || {};
        setGraph(data);
      })
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <div style={{
        padding: '10px 20px', borderBottom: '1px solid #e2e8f0', background: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Martian Architecture Explorer</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>Generated from the actual source -- see ARCHITECTURE.md at repo root for the full methodology.</div>
        </div>
        <a href="/" style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }}>&larr; Back to Martian</a>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {error && (
          <div style={{ padding: 24, color: '#b91c1c' }}>
            Failed to load the architecture graph: {error}. Run <code>python scripts/generate_architecture.py</code> from the repo root, then reload.
          </div>
        )}
        {!error && !graph && (
          <div style={{ padding: 24, color: '#64748b' }}>Loading architecture graph...</div>
        )}
        {graph && (
          <ReactFlowProvider>
            <GraphCanvas graph={graph} />
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
}
